import { useCallback, useEffect, useRef, useState } from "react";
import { analyticsService } from "../services/analytics/analyticsService";
import { AnalyticsConfig } from "../types/analytics";
import { CreateEventInput, AnalyticsCategory, UserBehaviorAction } from "../types/analyticsStandards";
import { isAnalyticsEnabled } from "../config/analyticsConfig";

/**
 * Configuration for useAnalytics hook
 */
interface UseAnalyticsConfig {
	userId: string; // User document ID (user.id)
	groupId: string; // User's group/organization (user.group)
	online: boolean; // Current online/offline state
	autoStart?: boolean; // Auto-start session on hook mount
	onError?: (error: Error) => void; // Error callback
	config?: Partial<AnalyticsConfig>; // Service configuration overrides
}

/**
 * Return type for useAnalytics hook - simplified with convenience methods
 */
interface UseAnalyticsReturn {
	// Status
	isEnabled: boolean;
	pendingEvents: number;
	isSyncing: boolean;
	lastSyncTime?: Date;
	syncError?: string;

	// Core tracking (detailed API - kept for advanced users)
	trackEvent: (input: CreateEventInput) => Promise<void>;

	// Session management
	startSession: () => Promise<void>;
	endSession: () => Promise<void>;

	// Simplified category-based tracking methods
	trackNavigationEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackRouteChange: (route: string, metadata?: Record<string, any>, shouldStartSession?: boolean) => Promise<void>;
	trackUIEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackContentEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackFeatureEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackSystemEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackUserBehaviorEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackProjectEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackFormBuilderEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackDataCollectionEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackReportingEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;
	trackCollaborationEvent: (action: string, metadata?: Record<string, any>) => Promise<void>;

	// Get stats
	getAnalyticsStats?: () => Promise<{ totalEvents: number; pendingEvents: number; syncedEvents: number }>;

	// Manual sync
	syncAnalytics: () => Promise<void>;

	// Debug utilities
	debugAnalytics?: () => Promise<void>;
}

/**
 * Simplified React hook for analytics tracking
 */
export const useAnalytics = ({ userId, groupId, online, autoStart = true, onError, config }: UseAnalyticsConfig): UseAnalyticsReturn => {
	const [pendingEvents, setPendingEvents] = useState(0);
	const [isSyncing, setIsSyncing] = useState(false);
	const [lastSyncTime, setLastSyncTime] = useState<Date | undefined>();
	const [syncError, setSyncError] = useState<string | undefined>();

	const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
	const sessionStartedRef = useRef<boolean>(false);
	const lastOnlineRef = useRef<boolean>(false);
	const isSyncingRef = useRef<boolean>(false);

	// Keep refs in sync with state
	useEffect(() => {
		isSyncingRef.current = isSyncing;
	}, [isSyncing]);

	// Initialize analytics service - SIMPLIFIED (no isEnabled complexity)
	useEffect(() => {
		const initializeAnalytics = async () => {
			try {
				await analyticsService.initialize(userId, groupId);

				// Get initial stats
				const stats = await analyticsService.getAnalyticsStats();
				setPendingEvents(stats.pendingEvents);

				// Start session if online and auto-start enabled
				if (autoStart && online && !sessionStartedRef.current) {
					await startSession();
				}
			} catch (error) {
				if (onError) {
					onError(error as Error);
				}
			}
		};

		// Always initialize if we have user context (no complex validation)
		if (userId && groupId) {
			initializeAnalytics();
		}

		return () => {
			// Cleanup
			if (syncTimerRef.current) {
				clearInterval(syncTimerRef.current);
			}
		};
	}, [userId, groupId, onError, config]);

	// Session management functions
	const startSession = useCallback(async () => {
		// Check if analytics is enabled before starting session
		if (!isAnalyticsEnabled()) {
			return;
		}

		if (sessionStartedRef.current) {
			return; // Session already started
		}

		// Add global session deduplication
		const globalAnalyticsSessionKey = `analytics_session_${userId}_${groupId}`;
		const lastAnalyticsSession = sessionStorage.getItem(globalAnalyticsSessionKey);
		const now = Date.now();
		const sessionTimeout = 30 * 60 * 1000; // 30 minutes

		if (lastAnalyticsSession && now - parseInt(lastAnalyticsSession) < sessionTimeout) {
			sessionStartedRef.current = true; // Mark as started to prevent retries
			return;
		}

		try {
			await analyticsService.trackEvent({
				category: AnalyticsCategory.USER_BEHAVIOR,
				action: UserBehaviorAction.SESSION_START,
				metadata: {
					userAgent: navigator.userAgent,
					screenResolution: `${screen.width}x${screen.height}`,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					onlineStatus: online,
				},
			});

			sessionStartedRef.current = true;
			// Update global analytics session tracker
			sessionStorage.setItem(globalAnalyticsSessionKey, now.toString());
		} catch (error) {
			console.error("Failed to start session:", error);
		}
	}, [online, userId, groupId, analyticsService]);

	const endSession = useCallback(async () => {
		// Check if analytics is enabled before ending session
		if (!isAnalyticsEnabled()) {
			return;
		}

		if (!sessionStartedRef.current) {
			return; // No active session
		}

		try {
			await analyticsService.trackEvent({
				category: AnalyticsCategory.USER_BEHAVIOR,
				action: UserBehaviorAction.SESSION_END,
				metadata: { onlineStatus: online },
			});

			sessionStartedRef.current = false;
		} catch (error) {
			console.error("Failed to end session:", error);
		}
	}, [online]);

	// Handle online/offline state changes
	useEffect(() => {
		const wasOnline = lastOnlineRef.current;
		lastOnlineRef.current = online;

		// User came online - start session if auto-start enabled
		if (online && !wasOnline && autoStart && userId && groupId) {
			startSession();
		}

		// User went offline - end session
		if (!online && wasOnline && sessionStartedRef.current) {
			endSession();
		}
	}, [online, autoStart, userId, groupId, startSession, endSession]);

	const syncAnalytics = useCallback(async () => {
		// Only sync if online and not already syncing
		if (!online || isSyncing) {
			return;
		}

		setIsSyncing(true);
		setSyncError(undefined);

		try {
			const result = await analyticsService.syncPendingEvents();

			// Update stats
			const stats = await analyticsService.getAnalyticsStats();
			setPendingEvents(stats.pendingEvents);

			setLastSyncTime(new Date());
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Sync failed";
			console.error("❌ Analytics sync failed:", error);
			setSyncError(errorMessage);
		} finally {
			setIsSyncing(false);
		}
	}, [online, isSyncing]);

	// Set up sync timer - depends on syncAnalytics so it gets fresh function reference
	useEffect(() => {
		// Set up sync timer (every 2 minutes)
		const intervalId = setInterval(
			() => {
				// Use ref to get current sync state (avoid stale closure)
				const currentlySyncing = isSyncingRef.current;
				if (online && !currentlySyncing && isAnalyticsEnabled()) {
					syncAnalytics();
				}
			},
			2 * 60 * 1000,
		); // 2 minutes

		// Store the interval ID for cleanup
		syncTimerRef.current = intervalId;

		return () => {
			if (syncTimerRef.current) {
				clearInterval(syncTimerRef.current);
				syncTimerRef.current = null;
			}
		};
	}, [online, syncAnalytics]); // Remove isSyncing from dependencies to prevent timer reset

	const trackEvent = useCallback(
		async (input: CreateEventInput) => {
			// Check if analytics is enabled before tracking
			if (!isAnalyticsEnabled()) {
				return;
			}

			try {
				await analyticsService.trackEvent(input);

				// Update pending count
				const stats = await analyticsService.getAnalyticsStats();
				setPendingEvents(stats.pendingEvents);
			} catch (error) {
				console.error("Failed to track event:", error);
				// Don't call onError for analytics failures - just log and continue
			}
		},
		[], // No dependencies needed - analytics always works if called
	);

	// ============================================================================
	// SIMPLIFIED CONVENIENCE METHODS - No imports needed!
	// ============================================================================

	const trackNavigationEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.NAVIGATION, action, metadata });
		},
		[trackEvent],
	);

	const trackUIEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.UI, action, metadata });
		},
		[trackEvent],
	);

	const trackContentEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.CONTENT, action, metadata });
		},
		[trackEvent],
	);

	const trackFeatureEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.FEATURE_USAGE, action, metadata });
		},
		[trackEvent],
	);

	const trackSystemEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.SYSTEM, action, metadata });
		},
		[trackEvent],
	);

	const trackUserBehaviorEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.USER_BEHAVIOR, action, metadata });
		},
		[trackEvent],
	);

	const trackProjectEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.PROJECT_MANAGEMENT, action, metadata });
		},
		[trackEvent],
	);

	const trackFormBuilderEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.FORM_BUILDER, action, metadata });
		},
		[trackEvent],
	);

	const trackDataCollectionEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.DATA_COLLECTION, action, metadata });
		},
		[trackEvent],
	);

	const trackReportingEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.REPORTING, action, metadata });
		},
		[trackEvent],
	);

	const trackCollaborationEvent = useCallback(
		async (action: string, metadata?: Record<string, any>) => {
			return trackEvent({ category: AnalyticsCategory.COLLABORATION, action, metadata });
		},
		[trackEvent],
	);

	// Special method for route changes - can trigger session start
	const trackRouteChange = useCallback(
		async (route: string, metadata?: Record<string, any>, shouldStartSession = true) => {
			// Start session if not already started and user wants it
			if (shouldStartSession && online && !sessionStartedRef.current) {
				await startSession();
			}

			return trackEvent({
				category: AnalyticsCategory.NAVIGATION,
				action: "route_change",
				metadata: { route, ...metadata },
			});
		},
		[trackEvent, online, startSession],
	);

	return {
		// Status
		isEnabled: isAnalyticsEnabled(), // Check actual analytics config
		pendingEvents,
		isSyncing,
		lastSyncTime,
		syncError,

		// Core detailed API (advanced users)
		trackEvent,

		// Session management
		startSession,
		endSession,

		// Simplified convenience methods (no imports needed!)
		trackNavigationEvent,
		trackRouteChange,
		trackUIEvent,
		trackContentEvent,
		trackFeatureEvent,
		trackSystemEvent,
		trackUserBehaviorEvent,
		trackProjectEvent,
		trackFormBuilderEvent,
		trackDataCollectionEvent,
		trackReportingEvent,
		trackCollaborationEvent,

		// Utilities
		syncAnalytics,
		getAnalyticsStats: analyticsService.getAnalyticsStats.bind(analyticsService),

		// Debug utilities
		debugAnalytics: async () => {
			console.log("=== ANALYTICS DEBUG ===");
			console.log("Analytics enabled:", isAnalyticsEnabled());
			console.log("Online:", online);
			console.log("Currently syncing:", isSyncing);
			console.log("Pending events:", pendingEvents);
			console.log("Last sync time:", lastSyncTime);
			console.log("Sync error:", syncError);
			console.log("Timer active:", !!syncTimerRef.current);

			try {
				const stats = await analyticsService.getAnalyticsStats();
				console.log("IndexedDB stats:", stats);

				// Test manual sync
				console.log("Testing manual sync...");
				await syncAnalytics();
				console.log("Manual sync test completed");
			} catch (error) {
				console.error("Debug failed:", error);
			}
			console.log("========================");
		},
	};
};
